import classNames from 'classnames';
import { IRow } from '../../types/table';
import { useState } from 'react';
import Loader from '../elements/Loader';

/*
*** IMPORTANT ***
 [key: string] should always be id or something
 else that is unique for the row that it represents.  
*/
interface Props {
  data: IRow;
  columns: {
    identifier: string;
    title: string;
  }[];
  onRowClick?: (id: string) => void;
  isFetchingCellData?: boolean;
  onCellClick?: (identifier: string, content: string) => void;
  dataTestIds?: {
    table?: string;
    row?: string;
  };
  className?: {
    header?: string;
    headerColumn?: string;
    table?: string;
  };
}
export const Table = ({
  data,
  columns,
  onRowClick,
  isFetchingCellData,
  onCellClick,
  dataTestIds = { table: 'table', row: 'row' },
  className,
}: Props) => {
  const [selectedCell, setSelectedCell] = useState<{
    key: string;
    data: string;
  }>({ key: '', data: '' });

  return (
    <div
      className={`overflow-auto max-h-[400px] w-full mx-auto shadow-md bg-white rounded-lg border border-gray-light ${className?.table}`}
    >
      <table
        className={`w-full text-sm text-left text-gray-dark transition-all rounded-lg`}
        data-test-id={dataTestIds.table}
      >
        <thead
          className={classNames(
            'text-xs font-normal text-gray-dark bg-gray-disabled border-0 border-b border-gray-light',
            className?.header,
          )}
        >
          <tr>
            {columns.map((headerColumn, index) => (
              <th
                key={headerColumn.identifier}
                scope='col'
                className={classNames(
                  'px-6 py-4',
                  className?.headerColumn,
                  index === columns.length - 1 && '!border-r-0',
                )}
              >
                <div className='flex items-center gap-2 w-max'>
                  <p>{headerColumn.title}</p>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.keys(data).map((key, index) => {
            const isLastRow = Object.values(data).length - 1 === index;

            return (
              <tr
                key={key}
                className={classNames(
                  `${!isLastRow && 'border-b'} hover:bg-gray-500/5`,
                  {
                    'cursor-pointer': !!onRowClick,
                  },
                )}
                onClick={!onRowClick ? () => null : () => onRowClick(key)}
                data-test-id={dataTestIds.row}
              >
                {Object.keys(data[key]).map((cellKey) => {
                  const isDownloadingFile =
                    isFetchingCellData &&
                    selectedCell.data ===
                      (data[key][cellKey] as unknown as string) &&
                    selectedCell.key === key;
                  return (
                    <td
                      key={`td_${key}_${cellKey}`}
                      className={`px-6 py-4 w-max`}
                      data-test-id={`td_${key}_${cellKey}`}
                    >
                      <div
                        className={classNames('text-black', {
                          'text-gray-900 no-underline cursor-default':
                            isDownloadingFile,
                          'cursor-pointer underline text-blue-500 font-medium':
                            !!onCellClick && cellKey === columns[2].identifier,
                        })}
                        onClick={
                          !onCellClick || isDownloadingFile
                            ? () => null
                            : () => {
                                onCellClick(cellKey, key),
                                  setSelectedCell({
                                    key: key,
                                    data: data[key][
                                      cellKey
                                    ] as unknown as string,
                                  });
                              }
                        }
                      >
                        {isDownloadingFile ? (
                          <div className='flex flex-row items-center gap-x-2'>
                            <Loader fillColor='#6923F4' />
                            <p className=''>Downloading file</p>
                          </div>
                        ) : (
                          data[key][cellKey]
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
